import { useEffect } from "react";
import { Star } from "lucide-react";
import ReviewsMarquee, { REVIEWS_ALL } from "../components/ReviewsMarquee";

/* WebView-friendly embed of the reviews strip.
   Self-contained: no nav, no footer, no page scroll. Locked to 380px
   height so the mobile-app card container can size around it. One
   right-to-left lane at landing-marquee speed, tap-to-pause enabled.

   Marked noindex so search engines don't crawl this surface. */
export default function ReviewsEmbed() {
    useEffect(() => {
        document.title = "TRT Plus · Reviews";

        const noindex = document.createElement("meta");
        noindex.setAttribute("name", "robots");
        noindex.setAttribute("content", "noindex, nofollow");
        document.head.appendChild(noindex);

        // Lock the body background and prevent page scroll — the embed
        // fills exactly 380px inside a WebView card, nothing else.
        // Solid #161919 (not black, not transparent) because WebView
        // transparency is unreliable on Android; a solid match to the
        // native container's backgroundColor eliminates any spinner-phase
        // colour flash.
        const prevBg = document.body.style.background;
        const prevOverflow = document.body.style.overflow;
        const prevMargin = document.body.style.margin;
        document.body.style.background = "#161919";
        document.body.style.overflow = "hidden";
        document.body.style.margin = "0";

        return () => {
            document.head.removeChild(noindex);
            document.body.style.background = prevBg;
            document.body.style.overflow = prevOverflow;
            document.body.style.margin = prevMargin;
        };
    }, []);

    return (
        <main
            data-testid="reviews-embed-page"
            className="relative text-[#f4f5f7] font-body"
            style={{ height: "380px", overflow: "hidden", backgroundColor: "#161919" }}
        >
            <div className="px-4 pt-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="eyebrow">
                        <Star
                            size={11}
                            strokeWidth={0}
                            fill="currentColor"
                            className="text-[#49bef5] drop-shadow-[0_0_6px_rgba(73,190,245,0.8)]"
                        />{" "}
                        Reviews
                    </div>
                </div>

                {/* Stat strip — mirrors the landing marquee header, compressed
                    for a card-width WebView. */}
                <div
                    className="flex items-stretch gap-0 border border-white/10 rounded-2xl bg-white/[0.02] overflow-hidden"
                    data-testid="reviews-embed-stats"
                >
                    <div className="flex-1 px-3 py-3 text-left">
                        <div className="flex items-center gap-1.5 font-display font-extrabold text-white text-lg leading-none">
                            4.5
                            <Star
                                size={14}
                                strokeWidth={0}
                                fill="currentColor"
                                className="text-[#49bef5] drop-shadow-[0_0_6px_rgba(73,190,245,0.6)]"
                            />
                        </div>
                        <div className="mt-1 text-[9px] uppercase tracking-[0.18em] font-mono text-[#49bef5]">
                            App Store
                        </div>
                    </div>
                    <div className="w-px bg-white/10" />
                    <div className="flex-1 px-3 py-3 text-left">
                        <div className="flex items-center gap-1.5 font-display font-extrabold text-white text-lg leading-none">
                            4.5
                            <Star
                                size={14}
                                strokeWidth={0}
                                fill="currentColor"
                                className="text-[#49bef5] drop-shadow-[0_0_6px_rgba(73,190,245,0.6)]"
                            />
                        </div>
                        <div className="mt-1 text-[9px] uppercase tracking-[0.18em] font-mono text-[#49bef5]">
                            Google Play
                        </div>
                    </div>
                    <div className="w-px bg-white/10" />
                    <div className="flex-1 px-3 py-3 text-left">
                        <div className="font-display font-extrabold text-white text-lg leading-none whitespace-nowrap">
                            Cross
                            <span className="text-[#49bef5]">·</span>
                            Platform
                        </div>
                        <div className="mt-1 text-[9px] uppercase tracking-[0.18em] font-mono text-[#49bef5]">
                            Multi-region
                        </div>
                    </div>
                </div>
            </div>

            {/* Single lane, right-to-left, landing speed. Interactive:
                native swipe scrubs the position, auto-scroll continues
                from wherever the user releases. Tap toggles pause and
                highlights the tapped card. */}
            <div className="mt-5">
                <ReviewsMarquee items={REVIEWS_ALL} speed={60} interactive />
            </div>
        </main>
    );
}
